import type { EditorValueProxy } from '@ui/MarkdownEditor/EditorValueProxy';
import type { SetStateAction, Dispatch, PropsWithChildren, FC } from 'react';

import { createContext, useContext, useMemo, useState } from 'react';

interface EditorValueProxyContextValue {
  /**
   * A reference to the editor's value proxy which can be used to serialize the editor value.
   */
  editorValueProxy: EditorValueProxy | null;
  setEditorValueProxy: Dispatch<SetStateAction<EditorValueProxy | null>>;
}

export const EditorValueProxyContext = createContext<EditorValueProxyContextValue | null>(null);

/**
 * Provides `EditorValueProxyContext` and makes it available to all children.
 */
export const EditorValueProxyContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [editorValueProxy, setEditorValueProxy] = useState<EditorValueProxyContextValue['editorValueProxy']>(null);

  return (
    <EditorValueProxyContext.Provider
      value={useMemo(
        () => ({
          editorValueProxy,
          setEditorValueProxy,
        }),
        [editorValueProxy],
      )}
    >
      {children}
    </EditorValueProxyContext.Provider>
  );
};

/**
 * Provides a reference to the editor's value proxy which can be used to serialize the editor value.
 * For performance reasons, the editor value is not serialized on every change, but only when the
 * form is submitted.
 */
export function useEditorValueProxyContext() {
  const value = useContext(EditorValueProxyContext);
  if (!value) throw new Error('EditorValueProxyContext.Provider is missing');
  return value;
}
